import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { MsalInterceptorConfiguration, MsalService, MSAL_INTERCEPTOR_CONFIG } from '@azure/msal-angular';
import { BrowserConfigurationAuthError, InteractionType, StringUtils, UrlString } from '@azure/msal-browser';

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {
    constructor(
        private authService: MsalService,
        private location: Location,
        @Inject(MSAL_INTERCEPTOR_CONFIG) private msalInterceptorConfig: MsalInterceptorConfiguration
    ) { }

    getScopesForEndpoint(endpoint: string, httpMethod: string) {
        const normalizedEndpoint = this.location.normalize(endpoint);
        const protectedResourcesArray = Array.from(this.msalInterceptorConfig.protectedResourceMap.keys());
        const matchingProtectedResources = this.matchResourcesToEndpoint(protectedResourcesArray, normalizedEndpoint);
        if (matchingProtectedResources.length > 0) {
            return this.matchScopesToEndpoint(this.msalInterceptorConfig.protectedResourceMap, matchingProtectedResources, httpMethod);
        }
        return null;
    }

    matchResourcesToEndpoint(protectedResourcesEndpoints: any[], endpoint: string) {
        return protectedResourcesEndpoints.filter((key) => {
            const normalizedKey = this.location.normalize(key);
            const keyComponents = new UrlString(key).getUrlComponents();
            const relativeNormalizedKey = keyComponents.QueryString
                ? keyComponents.AbsolutePath + '?' + keyComponents.QueryString
                : this.location.normalize(keyComponents.AbsolutePath);

            if (relativeNormalizedKey === '' || relativeNormalizedKey === '/*') {
                return StringUtils.matchPattern(normalizedKey, endpoint);
            }
            return StringUtils.matchPattern(normalizedKey, endpoint) || StringUtils.matchPattern(relativeNormalizedKey, endpoint);
        });
    }

    matchScopesToEndpoint(protectedResourceMap: any, endpointArray: any[], httpMethod: string) {
        const allMatchedScopes: any[] = [];

        endpointArray.forEach((matchedEndpoint) => {
            const scopesForEndpoint: string[] = [];
            const methodAndScopesArray = protectedResourceMap.get(matchedEndpoint);

            if (methodAndScopesArray === null) {
                allMatchedScopes.push(null);
                return;
            }

            methodAndScopesArray.forEach((entry: any) => {
                if (typeof entry === 'string') {
                    scopesForEndpoint.push(entry);
                } else {
                    const normalizedRequestMethod = httpMethod.toLowerCase();
                    const normalizedResourceMethod = entry.httpMethod.toLowerCase();
                    if (normalizedResourceMethod === normalizedRequestMethod) {
                        entry.scopes.forEach((scope: any) => scopesForEndpoint.push(scope));
                    }
                }
            });

            if (scopesForEndpoint.length > 0) {
                allMatchedScopes.push(scopesForEndpoint);
            }
        });

        return allMatchedScopes.length > 0 ? allMatchedScopes[0] : null;
    }

    idTokenExpired(expiryTime: any) {
        const exp = expiryTime * 1000;
        return new Date(exp) <= new Date();
    }

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (
            this.msalInterceptorConfig.interactionType !== InteractionType.Popup &&
            this.msalInterceptorConfig.interactionType !== InteractionType.Redirect
        ) {
            throw new BrowserConfigurationAuthError(
                'invalid_interaction_type',
                'Invalid interaction type provided to MSAL Interceptor.'
            );
        }

        const scopes = this.getScopesForEndpoint(req.url, req.method);

        if (!scopes || scopes.length === 0) {
            return next.handle(req);
        }

        const account = this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0];
        const authRequest =
            typeof this.msalInterceptorConfig.authRequest === 'function'
                ? this.msalInterceptorConfig.authRequest(this.authService, req, { account })
                : { ...this.msalInterceptorConfig.authRequest, account };

        const isForceRefresh = this.idTokenExpired(account?.idTokenClaims?.exp);

        return this.authService.acquireTokenSilent({ ...authRequest, scopes, account, forceRefresh: isForceRefresh }).pipe(
            catchError(() => this.authService.acquireTokenPopup({ ...authRequest, scopes })),
            switchMap((result) => (!result.idToken ? this.authService.acquireTokenPopup({ ...authRequest, scopes }) : of(result))),
            switchMap((result) => {
                const headers = req.headers.set('Authorization', 'Bearer ' + result.idToken);
                return next.handle(req.clone({ headers }));
            })
        );
    }
}